import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Settings, LogOut, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { roleLabel } from "@/lib/permissions/roles";
import { ChangePasswordDrawer } from "@/components/navigation/ChangePasswordDrawer";

function initials(name = "", email = "") {
  const source = name.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

export function ProfileMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login");
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="profile-trigger"
          className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <Avatar className="h-8 w-8 border border-border">
            <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
              {initials(user?.name, user?.email)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden text-sm font-medium text-foreground md:block">
            {user?.name || "Account"}
          </span>
          <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="flex items-center gap-3 px-2 py-2.5">
          <Avatar className="h-9 w-9 border border-border">
            <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
              {initials(user?.name, user?.email)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {user?.name}
            </p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <div className="px-2 pb-2">
          <span className="inline-flex rounded-full bg-brand-light px-2 py-0.5 text-xs font-medium text-brand-dark">
            {roleLabel(user?.role)}
          </span>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setPwOpen(true)} className="cursor-pointer gap-2.5" data-testid="profile-menu-change-password">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          Change Password
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/settings")}
          className="cursor-pointer gap-2.5"
          data-testid="profile-menu-settings"
        >
          <Settings className="h-4 w-4 text-muted-foreground" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={handleLogout}
          className="cursor-pointer gap-2.5 text-error focus:bg-error-light focus:text-error-dark"
          data-testid="profile-menu-logout"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <ChangePasswordDrawer open={pwOpen} onOpenChange={setPwOpen} />
    </>
  );
}
